/**
 * Standardized date handling utilities for IEP system
 * Handles both specific and vague dates while preserving original text
 */

export interface StandardizedDate {
  /** Original text as it appeared in the document */
  originalText: string
  /** Estimated date for sorting/processing (ISO string or null if unparseable) */
  estimatedDate: string | null
  /** Confidence level of the estimation */
  confidence: 'high' | 'medium' | 'low'
  /** Type of date reference */
  type: 'specific' | 'seasonal' | 'relative' | 'academic' | 'vague'
}

/**
 * Parse a date string and return standardized date information
 */
export const parseStandardizedDate = (dateStr: string | undefined | null): StandardizedDate | null => {
  if (!dateStr || typeof dateStr !== 'string') {
    return null
  }

  const originalText = dateStr.trim()
  const lowerText = originalText.toLowerCase()

  // Specific date patterns (high confidence)
  if (originalText.match(/^\d{4}-\d{2}-\d{2}/)) {
    return {
      originalText,
      estimatedDate: new Date(originalText).toISOString(),
      confidence: 'high',
      type: 'specific'
    }
  }

  if (originalText.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
    const [month, day, year] = originalText.split('/')
    return {
      originalText,
      estimatedDate: new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`).toISOString(),
      confidence: 'high',
      type: 'specific'
    }
  }

  // Extract year for various patterns
  const yearMatch = originalText.match(/(\d{4})/)?.[1]
  const currentYear = new Date().getFullYear()
  const year = yearMatch ? parseInt(yearMatch) : currentYear

  // Seasonal patterns (medium confidence)
  if (lowerText.includes('spring')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-03-15`).toISOString(),
      confidence: 'medium',
      type: 'seasonal'
    }
  }
  if (lowerText.includes('summer')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-07-15`).toISOString(),
      confidence: 'medium',
      type: 'seasonal'
    }
  }
  if (lowerText.includes('fall') || lowerText.includes('autumn')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-09-15`).toISOString(),
      confidence: 'medium',
      type: 'seasonal'
    }
  }
  if (lowerText.includes('winter')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-01-15`).toISOString(),
      confidence: 'medium',
      type: 'seasonal'
    }
  }

  // Monthly patterns (medium confidence)
  const monthNames = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ]
  
  for (let i = 0; i < monthNames.length; i++) {
    if (lowerText.includes(monthNames[i])) {
      return {
        originalText,
        estimatedDate: new Date(`${year}-${(i + 1).toString().padStart(2, '0')}-15`).toISOString(),
        confidence: 'medium',
        type: 'seasonal'
      }
    }
  }

  // Academic quarters (medium confidence)
  if (lowerText.includes('quarter 1') || lowerText.includes('q1')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-09-15`).toISOString(),
      confidence: 'medium',
      type: 'academic'
    }
  }
  if (lowerText.includes('quarter 2') || lowerText.includes('q2')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-12-15`).toISOString(),
      confidence: 'medium',
      type: 'academic'
    }
  }
  if (lowerText.includes('quarter 3') || lowerText.includes('q3')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-03-15`).toISOString(),
      confidence: 'medium',
      type: 'academic'
    }
  }
  if (lowerText.includes('quarter 4') || lowerText.includes('q4')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-06-15`).toISOString(),
      confidence: 'medium',
      type: 'academic'
    }
  }

  // Academic year patterns
  if (lowerText.includes('beginning of') && lowerText.includes('year')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-09-01`).toISOString(),
      confidence: 'medium',
      type: 'academic'
    }
  }
  if (lowerText.includes('end of') && lowerText.includes('year')) {
    return {
      originalText,
      estimatedDate: new Date(`${year}-06-30`).toISOString(),
      confidence: 'medium',
      type: 'academic'
    }
  }

  // Relative time patterns (low confidence)
  const now = new Date()
  
  // Week patterns
  const weeksMatch = originalText.match(/(\d+)\s*weeks?\s*(from\s*now|ago|later)/i)
  if (weeksMatch) {
    const weeks = parseInt(weeksMatch[1])
    const direction = weeksMatch[2].toLowerCase().includes('ago') ? -1 : 1
    const targetDate = new Date(now.getTime() + (weeks * 7 * 24 * 60 * 60 * 1000 * direction))
    return {
      originalText,
      estimatedDate: targetDate.toISOString(),
      confidence: 'low',
      type: 'relative'
    }
  }

  // Month patterns
  const monthsMatch = originalText.match(/(\d+)\s*months?\s*(from\s*now|ago|later)/i)
  if (monthsMatch) {
    const months = parseInt(monthsMatch[1])
    const direction = monthsMatch[2].toLowerCase().includes('ago') ? -1 : 1
    const targetDate = new Date(now)
    targetDate.setMonth(targetDate.getMonth() + (months * direction))
    return {
      originalText,
      estimatedDate: targetDate.toISOString(),
      confidence: 'low',
      type: 'relative'
    }
  }

  // Just a year
  if (originalText.match(/^\d{4}$/)) {
    return {
      originalText,
      estimatedDate: new Date(`${originalText}-06-15`).toISOString(),
      confidence: 'low',
      type: 'vague'
    }
  }

  // Fallback - return original text but no estimated date
  return {
    originalText,
    estimatedDate: null,
    confidence: 'low',
    type: 'vague'
  }
}

/**
 * Format a standardized date for display
 * Always shows the original text, optionally with estimated date info
 */
export const formatStandardizedDate = (
  dateInfo: StandardizedDate | null,
  options: {
    showEstimated?: boolean
    showConfidence?: boolean
  } = {}
): string => {
  if (!dateInfo) return 'Date not specified'

  let display = dateInfo.originalText

  if (options.showEstimated && dateInfo.estimatedDate) {
    const estimatedDisplay = new Date(dateInfo.estimatedDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
    
    if (dateInfo.confidence === 'high') {
      // For high confidence, don't show redundant info if it's the same
      const originalParsed = new Date(dateInfo.originalText)
      if (!isNaN(originalParsed.getTime())) {
        return display // Just show original for exact dates
      }
    }
    
    display += ` (≈${estimatedDisplay})`
  }

  if (options.showConfidence && process.env.NODE_ENV === 'development') {
    display += ` [${dateInfo.confidence}]`
  }

  return display
}

/**
 * Sort standardized dates chronologically (most recent first)
 */
export const sortStandardizedDates = (dates: (StandardizedDate | null)[]): (StandardizedDate | null)[] => {
  return dates.sort((a, b) => {
    // Nulls go to the end
    if (!a && !b) return 0
    if (!a) return 1
    if (!b) return -1

    // Items with estimated dates come first
    if (a.estimatedDate && !b.estimatedDate) return -1
    if (!a.estimatedDate && b.estimatedDate) return 1
    
    // Both have estimated dates - sort by date (most recent first)
    if (a.estimatedDate && b.estimatedDate) {
      return new Date(b.estimatedDate).getTime() - new Date(a.estimatedDate).getTime()
    }

    // Neither has estimated dates - sort by original text
    return a.originalText.localeCompare(b.originalText)
  })
}

/**
 * Get estimated Date object for sorting/processing
 */
export const getEstimatedDate = (dateInfo: StandardizedDate | null): Date | null => {
  if (!dateInfo || !dateInfo.estimatedDate) return null
  return new Date(dateInfo.estimatedDate)
}

/**
 * Check if two standardized dates likely refer to the same time period
 */
export const areDatesEquivalent = (date1: StandardizedDate | null, date2: StandardizedDate | null): boolean => {
  if (!date1 || !date2) return false
  
  // If original text is exactly the same
  if (date1.originalText.toLowerCase() === date2.originalText.toLowerCase()) return true
  
  // If both have estimated dates within a reasonable range
  if (date1.estimatedDate && date2.estimatedDate) {
    const diff = Math.abs(new Date(date1.estimatedDate).getTime() - new Date(date2.estimatedDate).getTime())
    const daysDiff = diff / (1000 * 60 * 60 * 24)
    
    // Consider equivalent if within 30 days for vague dates, 7 days for specific dates
    const threshold = (date1.confidence === 'high' && date2.confidence === 'high') ? 7 : 30
    return daysDiff <= threshold
  }
  
  return false
}

/**
 * Safely format a date string without timezone conversion issues
 * Specifically handles ISO dates (YYYY-MM-DD) that should be displayed as-is
 */
export const formatDateSafely = (
  dateString: string | undefined | null,
  options: {
    format?: 'short' | 'long' | 'numeric'
    fallback?: string
  } = {}
): string => {
  const { format = 'short', fallback = 'Date not specified' } = options
  
  if (!dateString || typeof dateString !== 'string') {
    return fallback
  }
  
  // Handle ISO date format (YYYY-MM-DD) - parse without timezone conversion
  const isoDateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch
    const date = {
      year: parseInt(year, 10),
      month: parseInt(month, 10),
      day: parseInt(day, 10)
    }
    
    const monthNames = {
      short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      long: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    }
    
    switch (format) {
      case 'long':
        return `${monthNames.long[date.month - 1]} ${date.day}, ${date.year}`
      case 'numeric':
        return `${date.month}/${date.day}/${date.year}`
      case 'short':
      default:
        return `${monthNames.short[date.month - 1]} ${date.day}, ${date.year}`
    }
  }
  
  // Handle MM/DD/YYYY format
  const usDateMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (usDateMatch) {
    const [, month, day, year] = usDateMatch
    const dateObj = {
      year: parseInt(year, 10),
      month: parseInt(month, 10),
      day: parseInt(day, 10)
    }
    
    const monthNames = {
      short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      long: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    }
    
    switch (format) {
      case 'long':
        return `${monthNames.long[dateObj.month - 1]} ${dateObj.day}, ${dateObj.year}`
      case 'numeric':
        return dateString // Return as-is for MM/DD/YYYY
      case 'short':
      default:
        return `${monthNames.short[dateObj.month - 1]} ${dateObj.day}, ${dateObj.year}`
    }
  }
  
  // For other formats, try to parse safely
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString // Return original if unparseable
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: format === 'long' ? 'long' : 'short',
      day: 'numeric'
    })
  } catch {
    return dateString // Return original on any error
  }
}
